<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    persistent
    :retain-focus="false"
    @keydown.esc="escape(closeDialog)"
  >
    <template v-if="isInitialized">
      <v-toolbar
        color="primaryBgr"
        dark
      >
        <v-btn icon dark tabindex="3" @click="escape(closeDialog)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ scheme ? $t('scheme.editScheme') : $t('scheme.createScheme') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn
            class="d-md-none"
            dark
            text
            tabindex="2"
            :disabled="!isModelChanged()"
            @click="submit"
          >
            {{ $t('save') }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card>
        <v-form ref="form" @submit.stop.prevent>
          <div class="p-dialog-full-screen-container">
            <v-row class="mb-8 mt-0">
              <v-col cols="12" md="7" lg="4">
                <v-text-field
                  type="text"
                  v-model.trim="editedScheme.name"
                  :rules="rules.name"
                  :label="$t('scheme.edit.title')"
                  class="p-input-lg"
                  tabindex="1"
                  clearable
                  autofocus
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5" lg="3">
                <v-btn
                  color="primary"
                  class="mt-4 hidden-sm-and-down"
                  :disabled="!isModelChanged()"
                  @click="submit"
                >
                  {{ $t('save') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col md="3">
                <p-search-input
                  v-model="search"
                  :items="searchSuggestions"
                ></p-search-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-data-table
                  v-model="editedScheme.users"
                  :headers="tableHeaders"
                  :items="searchedUsers"
                  :page.sync="page"
                  :items-per-page="itemsPerPage"
                  item-key="userId"
                  class="p-no-borders"
                  hide-default-footer
                  @page-count="pageCount = $event"
                >
                  <template v-slot:[`item.userId`]="{ item }">
                    <p-user-item
                      :value="managerById(item.userId)"
                      font-bold
                      you
                    ></p-user-item>
                  </template>
                  <template v-slot:[`item.budgetClaim`]="{ item }">
                    <access-switcher v-model="item.budgetClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.documentDocumentClaim`]="{ item }">
                    <access-switcher v-model="item.documentDocumentClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.documentContractClaim`]="{ item }">
                    <access-switcher v-model="item.documentContractClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.documentScheduleClaim`]="{ item }">
                    <access-switcher v-model="item.documentScheduleClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.eventClaim`]="{ item }">
                    <access-switcher v-model="item.eventClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.marketingPostClaim`]="{ item }">
                    <access-switcher v-model="item.marketingPostClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.memberClaim`]="{ item }">
                    <access-switcher v-model="item.memberClaim"></access-switcher>
                  </template>
                  <template v-slot:[`item.todoClaim`]="{ item }">
                    <access-switcher v-model="item.todoClaim"></access-switcher>
                  </template>

                </v-data-table>

                <v-pagination
                  v-model="page"
                  :length="pageCount"
                  :total-visible="15"
                  class="mt-5"
                ></v-pagination>
              </v-col>
            </v-row>
          </div>
        </v-form>
      </v-card>
    </template>
  </v-dialog>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PUserItem from '@/components/PUserItem'
import AccessSwitcher from './SchemeAccessSwitcher'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import errorMixin from '@/mixins/error.mixin'


export default {
  components: {
    PSearchInput,
    PUserItem,
    AccessSwitcher
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    errorMixin
  ],


  props: {
    scheme: Object
  },


  data () {
    return {
      isInitialized: false,
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      editedScheme: {},
      defaultScheme: {
        name: '',
        users: []
      },
      existingSchemeNames: []
    }
  },


  computed: {
    ...mapGetters('user', ['managerIds', 'managerById']),

    searchSuggestions () {
      return this.editedScheme.users.map(user => this.managerById(user.userId).fullName)
    },

    searchedUsers () {
      if (this.search) {
        return this.editedScheme.users.filter(user => this.managerById(user.userId).fullName.includes(this.search))
      }
      return this.editedScheme.users
    },

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required(),
          rule.unique(this.existingSchemeNames, this.$t('scheme.edit.titleExists'))
        ]
      }
    },

    tableHeaders () {
      return [
        { text: this.$t('scheme.table.name'), value: 'userId' },
        { text: this.$t('scheme.table.budget'), value: 'budgetClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.documentDocument'), value: 'documentDocumentClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.documentContract'), value: 'documentContractClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.documentSchedule'), value: 'documentScheduleClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.events'), value: 'eventClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.marketingPost'), value: 'marketingPostClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.member'), value: 'memberClaim', sortable: false, filterable: false },
        { text: this.$t('scheme.table.todos'), value: 'todoClaim', sortable: false, filterable: false }
      ]
    }
  },


  methods: {
    ...mapActions('scheme', ['createScheme', 'updateScheme']),
    ...mapActions('user', ['loadManagers']),

    async onDialogOpen () {
      this.isInitialized = false
      await this.loadManagers()
      this.existingSchemeNames = []
      this.editedScheme = this.getObjectCopy(this.scheme || this.defaultScheme)
      const schemeUsers = []

      for (const id of this.managerIds) {
        let schemeUser = this.editedScheme.users.find(x => x.userId === id)

        if (!schemeUser) {
          schemeUser = {
            userId: id,
            budgetClaim: true,
            documentDocumentClaim: true,
            documentContractClaim: true,
            documentScheduleClaim: true,
            eventClaim: true,
            marketingPostClaim: true,
            memberClaim: true,
            todoClaim: true
          }
        }
        schemeUsers.push(schemeUser)
      }

      this.editedScheme.users = [...schemeUsers]
      this.isInitialized = true
      this.initWatchedModel(this.editedScheme)
      this.resetValidation()
    },


    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()
        const scheme = this.getObjectCopy(this.editedScheme)
        if (scheme.id) {
          await this.updateScheme(scheme)
          this.showSnackbarSuccess(this.$t('scheme.schemeUpdated'))
        } else {
          await this.createScheme(scheme)
          this.showSnackbarSuccess(this.$t('scheme.schemeCreated'))
        }

        this.closeDialog()
      } catch (error) {
        if (this.is409ConflictError(error)) {
          this.hideSnackbar()
          this.existingSchemeNames.push(this.editedScheme.name)
          this.validate(this.rulesToApply)
        } else {
          this.showSnackbarError()
        }
        this.$log.error(error)
      }
    },


    close () {
      this.$emit('update:dialog', false)
    }
  }
}
</script>
